import { useCallback } from 'react';
import { NavigateOptions, useNavigate } from 'react-router-dom';

import { useSelectedApplicationGroupId } from './useSelectedApplicationGroupId';
import { useSelectedApplicationId } from './useSelectedApplicationId';

/**
 * Custom hook that provides navigation functions which ensure selection state
 * updates are applied before navigation occurs.
 */
export const useNavigateWithState = () => {
  const navigate = useNavigate();
  const { setSelectedGroupId } = useSelectedApplicationGroupId();
  const { setSelectedApplicationId } = useSelectedApplicationId();

  /**
   * Navigate to a path after setting the application group and application IDs.
   */
  const navigateWithApplicationContext = useCallback(
    (
      groupId: string,
      appId: string,
      path: string,
      options?: NavigateOptions
    ) => {
      setSelectedGroupId(groupId);
      setSelectedApplicationId(appId);
      navigate(path, options);
    },
    [navigate, setSelectedGroupId, setSelectedApplicationId]
  );

  /**
   * Navigate to a path after setting only the application group ID.
   */
  const navigateWithGroupContext = useCallback(
    (groupId: string, path: string, options?: NavigateOptions) => {
      setSelectedGroupId(groupId);
      navigate(path, options);
    },
    [navigate, setSelectedGroupId]
  );

  return {
    navigateWithApplicationContext,
    navigateWithGroupContext,
  };
};
